<template>
  <div class="min-w-96 pb-14">

    <div class="flex flex-col items-center">
      <div>You've completed <span class="text-green-400">{{ score }}</span> today!</div>

      <!-- Run report card: a recap of how this run went, from stats the engine tallies as you
           play. Gives the run a satisfying button-end and gives the share something to back up. -->
      <div v-if="report" class="grid grid-cols-2 gap-2 w-full max-w-xs mt-4">
        <div class="bg-gray-800 rounded-lg px-3 py-2 flex flex-col items-center">
          <span class="text-[11px] uppercase tracking-wide text-gray-400">XP earned</span>
          <span class="text-xl font-extrabold text-gradient">+{{ report.xpEarned }}</span>
        </div>
        <div class="bg-gray-800 rounded-lg px-3 py-2 flex flex-col items-center">
          <span class="text-[11px] uppercase tracking-wide text-gray-400">Accuracy</span>
          <span class="text-xl font-extrabold text-gradient">{{ report.accuracy }}%</span>
        </div>
        <div class="bg-gray-800 rounded-lg px-3 py-2 flex flex-col items-center">
          <span class="text-[11px] uppercase tracking-wide text-gray-400">Best momentum</span>
          <span class="text-xl font-extrabold text-gradient">&times;{{ report.bestMultiplier }}</span>
        </div>
        <div class="bg-gray-800 rounded-lg px-3 py-2 flex flex-col items-center">
          <span class="text-[11px] uppercase tracking-wide text-gray-400">Flawless</span>
          <span class="text-xl font-extrabold text-gradient">{{ report.flawless }}</span>
        </div>
      </div>
    </div>
    <div class="mt-4 text-lg">NEXT PUZZLE IN</div>
    <div class="text-4xl">{{hoursUntilTomorrow}}:{{minutesUntilTomorrow}}:{{secondsUntilTomorrow}}</div>

    <button class="mt-2 bg-gradient-to-r from-green-400 to-teal-400 hover:bg-green-500 text-xl text-gray-900 w-60 py-2" @click="share()">{{ copied ? 'Copied!' : 'Share' }}</button>
    <Leaderboard class="w-40"></Leaderboard>
  </div>
</template>
<script setup>

import Leaderboard from "./Leaderboard.vue";
import {ref} from "vue";
import {useAchievements} from "../composables/useAchievements.js";
import {safeFetch} from "../utils/http.js";

const {unlock} = useAchievements();

const props = defineProps({
  score: Number,
  // Run recap stats from the game engine: { xpEarned, accuracy, bestMultiplier, flawless }.
  report: {type: Object, default: null},
})

let hoursUntilTomorrow = ref('00');
let minutesUntilTomorrow = ref('00');
let secondsUntilTomorrow = ref('00');

setInterval(() => {
  let now = new Date();
  let nowUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());
  let tomorrow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));

  let millisecondsUntilTomorrow = tomorrow - nowUtc;

  secondsUntilTomorrow.value = Math.floor(millisecondsUntilTomorrow / 1000);
  minutesUntilTomorrow.value = Math.floor(secondsUntilTomorrow.value / 60);
  hoursUntilTomorrow.value = Math.floor(minutesUntilTomorrow.value / 60);

  hoursUntilTomorrow.value = String(hoursUntilTomorrow.value).padStart(2, '0');
  minutesUntilTomorrow.value = String(minutesUntilTomorrow.value % 60).padStart(2, '0')
  secondsUntilTomorrow.value = String(secondsUntilTomorrow.value % 60).padStart(2, '0');

}, 1000)

const url = 'https://syndles.com';
let copied = ref(false);

// A spoiler-free progress grid: one green square per puzzle cleared, laid out 10 per row,
// so the result reads as a shape that grows with the run, without leaking any answers. The
// run is effectively endless, so the grid is sized to what you solved rather than to a cap.
function buildShareGrid() {
  const COLS = 10;
  const cleared = Math.max(0, props.score);
  if (cleared === 0) return '';
  const lines = [];
  for (let r = 0; r < Math.ceil(cleared / COLS); r++) {
    const inRow = Math.min(COLS, cleared - r * COLS);
    lines.push('🟩'.repeat(inRow));
  }
  return lines.join('\n');
}

function buildShareText() {
  const puzzles = `${props.score} ${props.score === 1 ? 'puzzle' : 'puzzles'}`;
  const headline = `I solved ${puzzles} today 🏆`;
  const grid = buildShareGrid();
  const lines = [`Syndles ${formatUTCDate()}`, headline];
  if (grid) lines.push(grid);
  lines.push('Think you can beat me?');
  return lines.join('\n');
}

async function share() {
  const text = buildShareText();

  // Only hand off to the native share sheet on touch devices. Desktop Chrome/Edge also
  // expose navigator.share, but it opens a clunky OS dialog, so on a mouse-driven device
  // we'd rather just copy to the clipboard (Wordle-style). A thrown AbortError means the
  // user dismissed the sheet, so don't fall through to clipboard in that case.
  const isTouch = window.matchMedia('(pointer: coarse)').matches;
  if (navigator.share && isTouch) {
    try {
      await navigator.share({title: 'Syndles', text, url});
      recordShare();
      return;
    } catch (e) {
      if (e.name === 'AbortError') return;
    }
  }

  // Desktop (and anything without the Share API): copy to clipboard and confirm.
  try {
    await navigator.clipboard.writeText(`${text}\n${url}`);
    copied.value = true;
    setTimeout(() => copied.value = false, 2000);
    recordShare();
  } catch (e) {
    alert('Could not copy your result to the clipboard.');
  }
}

// Tell the server the player shared, so it can award the "Spread the Word" badge (and toast it
// the first time). Fire-and-forget: a failed award must never disrupt the share itself.
async function recordShare() {
  const userId = localStorage.getItem("userId");
  if (!userId) return;
  const response = await safeFetch(`${import.meta.env.VITE_APP_API_BASE_URL}/api/users/${userId}/achievements/share`, {method: 'POST'});
  if (response?.ok) {
    const json = await response.json();
    unlock(json.newAchievements);
  }
}

function formatUTCDate() {
  const date = new Date();
  const year = date.getUTCFullYear().toString().slice(-2);
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  return `${day}/${month}/${year}`;
}
</script>


<style scoped>

</style>